import React, { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import PermissionLink from '../../components/PermissionLink';
import { getParam, ParamRowDetail } from '../../api/parameter';
import { useCategoryPairOptions } from '../../utils/categoryOptions';
import { absoluteURL, ApiError } from '../../api/request';
import { formatTime } from '../../utils/time';
import { useDictText } from '../../components/DictSelect';
import { useScopedDictText } from '../../utils/dictOptions';

// 效果图/矩阵图 详情页 (2026-08-14 口径变更: 资产内嵌参数行, 本页按 arch+行id 读参数行渲染).
// 路由: /gallery/asset/:arch/:id?kind=matrix|effect
// 只读预览: 大图 + 加工条件 + 矩阵元数据; 顶部「查看参数行」跳参数页 (是否可编辑由参数页按 RBAC 决定).

const ARCH_GRADIENT: Record<string, string> = {
    'laser-frame': 'linear-gradient(135deg,#d9b382,#a87b4a)',
    'laser-galvo': 'linear-gradient(135deg,#ffd700,#b8860b)',
    knife: 'linear-gradient(135deg,#cbd5e1,#94a3b8)',
    thermal: 'linear-gradient(135deg,#fca5a5,#ef4444)',
    embroidery: 'linear-gradient(135deg,#a7f3d0,#10b981)',
    print: 'linear-gradient(135deg,#bfdbfe,#3b82f6)',
};
const DEFAULT_GRADIENT = 'linear-gradient(135deg,#e2e8f0,#cbd5e1)';

// 光源码候选 (与后端 device_key 推导规则一致: 倒数第二段).
const LIGHT_TOKENS = new Set(['blue', 'red', 'co2', 'ir', 'uv', 'green']);
function lightFromDeviceKey(deviceKey?: string): string {
    const seg = (deviceKey || '').split('-');
    const token = seg.length >= 2 ? seg[seg.length - 2] : '';
    return LIGHT_TOKENS.has(token) ? token : '';
}

// 矩阵元数据: paraMatrix 解析后结构化展示 (概览 + 逐格网格), 原始 JSON 收进折叠.
interface MatrixCell {
    row?: number;
    col?: number;
    power?: number;
    speed?: number;
    color?: string;
}

function parseMatrix(json?: string): Record<string, unknown> | null {
    if (!json || json === 'null') return null;
    try {
        const o = JSON.parse(json) as Record<string, unknown>;
        return o && typeof o === 'object' ? o : null;
    } catch {
        return null;
    }
}

/** 逐格数据: cutPenetrate / engraveColors / scoreColors 任一 (可能是二次编码的 JSON 字符串). */
function matrixCells(m: Record<string, unknown>): MatrixCell[] {
    for (const key of ['cutPenetrate', 'engraveColors', 'scoreColors']) {
        let v = m[key];
        if (typeof v === 'string') {
            try {
                v = JSON.parse(v);
            } catch {
                continue;
            }
        }
        if (Array.isArray(v) && v.length > 0) return v as MatrixCell[];
    }
    return [];
}

function MatrixPanel({
    matrixJson,
    processTypeLabel,
}: {
    matrixJson: string;
    processTypeLabel: (code: string) => string;
}) {
    const m = parseMatrix(matrixJson);
    if (!m) return null;
    const rows = Number(m.row) || 0;
    const cols = Number(m.col) || 0;
    const cells = matrixCells(m);
    const cellAt = new Map(
        cells.map((c) => [`${c.row ?? 0}-${c.col ?? 0}`, c]),
    );

    // 轴模式: 常规矩阵每列功率一致、每行速度一致 → 列头=功率 / 行头=速度,
    // 格子只做强度渐变 (与实物矩阵图的读法一致); 不规则数据退化为逐格数值模式.
    const colPower: (number | undefined)[] = [];
    const rowSpeed: (number | undefined)[] = [];
    let axisMode = rows > 0 && cols > 0 && cells.length === rows * cols;
    if (axisMode) {
        for (let ci = 0; ci < cols && axisMode; ci++) {
            const vals = new Set<number | undefined>();
            for (let ri = 0; ri < rows; ri++)
                vals.add(cellAt.get(`${ri}-${ci}`)?.power);
            if (vals.size !== 1) axisMode = false;
            else colPower[ci] = [...vals][0];
        }
        for (let ri = 0; ri < rows && axisMode; ri++) {
            const vals = new Set<number | undefined>();
            for (let ci = 0; ci < cols; ci++)
                vals.add(cellAt.get(`${ri}-${ci}`)?.speed);
            if (vals.size !== 1) axisMode = false;
            else rowSpeed[ri] = [...vals][0];
        }
    }

    const facts: [string, React.ReactNode][] = [];
    if (rows && cols) facts.push(['规格', `${rows} × ${cols}`]);
    if (m.minPower !== undefined)
        facts.push(['功率', `${m.minPower} – ${m.maxPower}`]);
    if (m.minSpeed !== undefined)
        facts.push(['速度', `${m.minSpeed} – ${m.maxSpeed}`]);
    if (m.repeat !== undefined) facts.push(['加工次数', String(m.repeat)]);
    if (m.processingMode) facts.push(['加工模式', String(m.processingMode)]);
    if (m.processingType)
        facts.push(['加工类型', processTypeLabel(String(m.processingType))]);

    const cellBg = (ri: number, ci: number): string => {
        const c = cellAt.get(`${ri}-${ci}`);
        if (c?.color) return c.color;
        // 功率随列增强的紫色渐变, 传达"往右功率越大"
        const t = cols > 1 ? ci / (cols - 1) : 0.5;
        return `rgba(124, 58, 237, ${(0.12 + 0.4 * t).toFixed(2)})`;
    };

    return (
        <div className="card p-4 space-y-4">
            <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">矩阵元数据</span>
                <span className="tag bg-violet-50 text-violet-700">
                    参数级矩阵图
                </span>
            </div>

            {/* 概览: 信息药丸 */}
            <div className="flex flex-wrap gap-2">
                {facts.map(([k, v]) => (
                    <div
                        key={k}
                        className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-1.5"
                    >
                        <div className="text-[10px] text-slate-400 leading-none mb-1">
                            {k}
                        </div>
                        <div className="text-[13px] font-semibold leading-none">
                            {v}
                        </div>
                    </div>
                ))}
            </div>

            {rows > 0 && cols > 0 && cells.length > 0 && (
                <div className="overflow-x-auto">
                    {axisMode ? (
                        // 轴模式: 与实物矩阵图同构 (列=功率, 行=速度), 格子按功率渐变
                        <table
                            style={{
                                borderCollapse: 'separate',
                                borderSpacing: 4,
                            }}
                        >
                            <thead>
                                <tr>
                                    <th className="text-[10px] text-slate-400 font-normal text-right pr-1 align-bottom leading-tight">
                                        功率 →<br />
                                        速度 ↓
                                    </th>
                                    {colPower.map((pv, ci) => (
                                        <th
                                            key={ci}
                                            className="text-xs font-semibold text-violet-700 text-center px-1 pb-0.5"
                                        >
                                            {pv ?? '—'}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {Array.from({ length: rows }, (_, ri) => (
                                    <tr key={ri}>
                                        <td className="text-xs font-semibold text-violet-700 text-right pr-1">
                                            {rowSpeed[ri] ?? '—'}
                                        </td>
                                        {Array.from(
                                            { length: cols },
                                            (_, ci) => {
                                                const c = cellAt.get(
                                                    `${ri}-${ci}`,
                                                );
                                                return (
                                                    <td key={ci}>
                                                        <div
                                                            className="rounded-md"
                                                            style={{
                                                                width: 40,
                                                                height: 26,
                                                                background:
                                                                    cellBg(
                                                                        ri,
                                                                        ci,
                                                                    ),
                                                            }}
                                                            title={`功率 ${c?.power ?? '—'} · 速度 ${c?.speed ?? '—'}`}
                                                        />
                                                    </td>
                                                );
                                            },
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        // 不规则数据: 逐格数值
                        <div
                            className="inline-grid gap-1"
                            style={{
                                gridTemplateColumns: `repeat(${cols}, minmax(56px, auto))`,
                            }}
                        >
                            {Array.from({ length: rows * cols }, (_, i) => {
                                const c = cellAt.get(
                                    `${Math.floor(i / cols)}-${i % cols}`,
                                );
                                return (
                                    <div
                                        key={i}
                                        className="rounded-md bg-slate-50 border border-slate-100 px-1.5 py-1 text-center text-[11px] leading-tight"
                                        style={
                                            c?.color
                                                ? {
                                                      borderColor: c.color,
                                                      borderWidth: 2,
                                                  }
                                                : undefined
                                        }
                                    >
                                        <div className="font-semibold">
                                            {c?.power ?? '—'}
                                        </div>
                                        <div className="text-slate-400">
                                            {c?.speed ?? '—'}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    <div className="text-[11px] text-slate-400 mt-1.5">
                        {axisMode
                            ? '列 = 功率, 行 = 速度 (源数据原始值, 单位随设备定义); 悬停格子看数值, 颜色矩阵的格子显示采样色'
                            : '每格 上:功率 / 下:速度 (源数据原始值, 单位随设备定义)'}
                    </div>
                </div>
            )}

            <details className="text-[11px] text-slate-400">
                <summary className="cursor-pointer select-none">
                    原始 JSON
                </summary>
                <pre
                    className="bg-slate-50 rounded p-3 overflow-auto mt-1"
                    style={{ maxHeight: 200 }}
                >
                    {JSON.stringify(m, null, 2)}
                </pre>
            </details>
        </div>
    );
}

function InfoRow({ k, v }: { k: string; v: React.ReactNode }) {
    return (
        <div className="flex items-baseline justify-between text-sm py-1.5 border-b border-slate-50 last:border-b-0">
            <span className="text-slate-500">{k}</span>
            <span className="text-slate-700 text-right">{v ?? '—'}</span>
        </div>
    );
}

function SampleDetail() {
    const { arch, id } = useParams<{ arch: string; id: string }>();
    const location = useLocation();
    // kind 决定 返回 tab / 主图来源; 缺省按数据推断 (有 paraMatrix → 矩阵)
    const kindQ = new URLSearchParams(location.search).get('kind');
    const [row, setRow] = useState<ParamRowDetail | null>(null);
    // inkjet 行的材料键 = (一级, 二级) 类别, 显示「一级 / 二级」中文名
    const catPair = useCategoryPairOptions(Boolean(row?.type2));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    // 光源展示中文名 (字典 light_source), 详情不露 code
    const lightText = useDictText('light_source');
    // 加工类型展示字典 label (process_type, scope=arch), 与字典维护页命名/文案统一
    const processTypeText = useScopedDictText('process_type', [arch]);

    useEffect(() => {
        if (!arch || !id) return;
        const numId = Number(id);
        if (!Number.isFinite(numId)) {
            setError('非法 id');
            return;
        }
        setLoading(true);
        getParam(arch, numId)
            .then((d) => {
                setRow(d);
                setError('');
            })
            .catch((e) => {
                const msg =
                    e instanceof ApiError
                        ? `${e.code}: ${e.message}`
                        : String(e);
                setError(msg);
                setRow(null);
            })
            .finally(() => setLoading(false));
    }, [arch, id]);

    const hasMatrix = Boolean(row?.paraMatrix && row.paraMatrix !== 'null');
    const isMatrix = kindQ ? kindQ === 'matrix' : hasMatrix;

    if (loading) {
        return <div className="text-sm text-slate-500">加载资产详情…</div>;
    }
    if (error || !row) {
        return (
            <div className="space-y-4">
                <Link
                    to={isMatrix ? '/gallery/matrix' : '/gallery'}
                    className="text-sm text-brand-600 hover:underline"
                >
                    ← {isMatrix ? '矩阵图' : '效果图库'}
                </Link>
                <div className="card p-4 text-sm text-rose-600 border-rose-200 bg-rose-50">
                    {error || '资产不存在 (来源参数行可能已删除)'}
                </div>
            </div>
        );
    }

    const gradient = ARCH_GRADIENT[arch] || DEFAULT_GRADIENT;
    const xcode = row.xcode || row.filmXcode || '';
    const matrix = parseMatrix(row.paraMatrix);
    // 主图: 矩阵图 = paraMatrix.frontImg / 效果图 = effectImage
    const imageUrl = isMatrix
        ? String(matrix?.frontImg || '')
        : row.effectImage || '';
    const editLink = `/param-sets/manage?arch=${encodeURIComponent(arch)}&id=${row.id}`;

    return (
        <div className="space-y-5">
            {/* 顶部面包屑 + 操作 */}
            <div className="flex items-center gap-3 flex-wrap">
                <Link
                    to={isMatrix ? '/gallery/matrix' : '/gallery'}
                    className="text-sm text-brand-600 hover:underline"
                >
                    ← {isMatrix ? '矩阵图' : '效果图库'}
                </Link>
                <span className="text-slate-300">·</span>
                <span className="font-mono text-xs text-slate-500">
                    {arch} · 参数行 #{row.id}
                </span>
                <span
                    className={`tag ${isMatrix ? 'bg-violet-50 text-violet-700' : 'bg-brand-50 text-brand-700'}`}
                >
                    {isMatrix ? '矩阵图' : '效果图'}
                </span>
                <Link
                    to={editLink}
                    className="ml-auto text-sm text-brand-600 hover:underline"
                >
                    查看参数行 →
                </Link>
            </div>

            <div className="grid grid-cols-3 gap-4">
                {/* 图片 */}
                <div className="card col-span-2 overflow-hidden">
                    {imageUrl ? (
                        <img
                            src={absoluteURL(imageUrl)}
                            alt={xcode || `param-${row.id}`}
                            style={{
                                display: 'block',
                                width: '100%',
                                maxHeight: 480,
                                objectFit: 'contain',
                                background: '#f8fafc',
                            }}
                        />
                    ) : (
                        <div style={{ height: 360, background: gradient }} />
                    )}
                    <div
                        className="px-4 py-2 text-xs text-slate-400 border-t border-slate-100 font-mono truncate"
                        title={imageUrl}
                    >
                        {imageUrl || '暂无图片 URL'}
                    </div>
                </div>

                {/* 加工条件 + 元信息 */}
                <div className="card p-4 col-span-1 space-y-1.5">
                    <div className="text-sm font-semibold mb-2">加工条件</div>
                    <InfoRow
                        k="工艺架构"
                        v={
                            <span className="tag bg-brand-50 text-brand-700">
                                {arch}
                            </span>
                        }
                    />
                    <InfoRow
                        k="设备"
                        v={
                            <span className="font-mono text-xs">
                                {row.deviceKey}
                            </span>
                        }
                    />
                    <InfoRow
                        k="材料"
                        v={
                            xcode ? (
                                <PermissionLink
                                    resource="material"
                                    to={`/materials/${encodeURIComponent(xcode)}`}
                                    className="text-brand-600 hover:underline font-mono text-xs"
                                >
                                    {xcode}
                                </PermissionLink>
                            ) : (
                                catPair.text(row.type1, row.type2) || '—'
                            )
                        }
                    />
                    {row.substrateXcode && (
                        <InfoRow
                            k="基材"
                            v={
                                <PermissionLink
                                    resource="material"
                                    to={`/materials/${encodeURIComponent(row.substrateXcode)}`}
                                    className="text-brand-600 hover:underline font-mono text-xs"
                                >
                                    {row.substrateXcode}
                                </PermissionLink>
                            }
                        />
                    )}
                    <InfoRow
                        k="光源"
                        v={lightText(lightFromDeviceKey(row.deviceKey))}
                    />
                    <InfoRow
                        k="加工类型"
                        v={processTypeText(arch, row.subProcess)}
                    />
                    <InfoRow k="状态" v={row.status} />
                    <InfoRow k="备注" v={row.remark} />
                    <InfoRow
                        k="更新时间"
                        v={
                            row.updatedAt && (
                                <span className="text-xs text-slate-500">
                                    {formatTime(row.updatedAt)}
                                </span>
                            )
                        }
                    />
                </div>
            </div>

            {/* 矩阵元数据 (效果图条目若同行带矩阵也一并展示) */}
            {hasMatrix && row.paraMatrix && (
                <MatrixPanel
                    matrixJson={row.paraMatrix}
                    processTypeLabel={(code) => processTypeText(arch, code)}
                />
            )}
        </div>
    );
}

export default SampleDetail;
